import { useCallback, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import isEqual from 'lodash/isEqual'

import {
  selectCloneLeaguesSession,
  selectCloneLeaguesSessionID,
  selectCloneLeaguesSessionData,
  selectSessionSaverLoading,
  selectSessionSaverError,
  updateLeagueConfig,
  updateAllLeaguesConfigs,
  syncSelectedLeaguesToSession,
  syncEnhancedLeaguesToSession,
  saveSession,
} from '../reducers/session_saver'
import { selectSelectedLeaguesForCloning } from '../reducers/selected_leagues_for_cloning'
import { selectCloningStep } from '../reducers/cloning_steps'
import { selectProductType, selectTextSearch } from '../reducers/leagues_filter_values'
import { selectConfigsOptions, selectConfigsSeason } from '../reducers/leagues_configs'
import { selectEnhancedLeagues } from '../reducers/enhanced_leagues'
import {
  translateTimeSeries,
  calculateFutureEquivalentDate,
  parseDateUTC,
  todayUTC,
  toISODateString,
} from '../timeUtils'
import { ALL_ENTRIES_FILTER_VALUE, CloningStepsEnum } from '../constants'
import { startCloneLeaguesSession } from '../reducers/clone_league_session_starter'

const useSessionSaver = () => {
  const dispatch = useDispatch()

  const cloningStep = useSelector(selectCloningStep)
  const session = useSelector(selectCloneLeaguesSession)
  const sessionID = useSelector(selectCloneLeaguesSessionID)
  const sessionData = useSelector(selectCloneLeaguesSessionData, isEqual)
  const isLoading = useSelector(selectSessionSaverLoading)
  const error = useSelector(selectSessionSaverError)
  const selectedLeagues = useSelector(selectSelectedLeaguesForCloning)
  const configsOptions = useSelector(selectConfigsOptions)
  const configsSeason = useSelector(selectConfigsSeason)
  const enhancedLeagues = useSelector(selectEnhancedLeagues)
  const textSearch = useSelector(selectTextSearch)
  const productType = useSelector(selectProductType)

  const mergedLeagues = useMemo(() => {
    const sessionLeagues = sessionData?.leagues || {}
    const enhancedLeaguesByID = (enhancedLeagues || []).reduce((acc, league) => {
      acc[String(league.id)] = league
      return acc
    }, {})

    return Object.entries(sessionLeagues).reduce((acc, [ id, sessionLeague ]) => {
      acc[id] = {
        ...enhancedLeaguesByID[id],
        ...sessionLeague,
      }
      return acc
    }, {})
  }, [ sessionData, enhancedLeagues ])

  // STEP 1 - Save selected leagues
  const saveSelectedLeaguesToSession = useCallback(() => {
    dispatch(syncSelectedLeaguesToSession(selectedLeagues))
  }, [ dispatch, selectedLeagues ])

  // STEP 3 - Save selected configs
  // Uses the inner-thunk pattern so the Redux write and server POST are atomic:
  // reads the updated session from the store immediately after the local dispatch,
  // avoiding the race where sessionData (isEqual) hasn't changed and the
  // pendingSaveRef useEffect never fires.
  const saveConfigsToSession = useCallback(() => {
    dispatch((innerDispatch, getState) => {
      updateAllLeaguesConfigs({ ...configsOptions, season: configsSeason.value })(innerDispatch, getState)
      const updatedSession = selectCloneLeaguesSession(getState())
      innerDispatch(saveSession({
        cloneLeaguesSessionID: updatedSession?.id,
        data: { ...updatedSession?.data, cloningStep: cloningStep },
      }))
    })
  }, [ dispatch, configsOptions, configsSeason.value, cloningStep ])

  const saveSessionToServer = useCallback(() => {
    dispatch((dispatch, getState) => {
      syncSelectedLeaguesToSession(selectedLeagues)(dispatch, getState)
      updateAllLeaguesConfigs({ ...configsOptions, season: configsSeason.value })(dispatch, getState)
      const updatedSession = selectCloneLeaguesSession(getState())
      dispatch(saveSession({
        cloneLeaguesSessionID: updatedSession?.id,
        data: { ...updatedSession?.data, cloningStep: cloningStep },
      }))
    })
  }, [ dispatch, selectedLeagues, configsOptions, configsSeason.value, cloningStep ])

  // Step 4 - Leagues for date selection
  const leaguesForDatePicking = useMemo(() => {
    return Object.entries(mergedLeagues).reduce((acc, [ id, league ]) => {
      if (textSearch && !league.name?.toLowerCase().includes(textSearch.toLowerCase())) {
        return acc
      }
      if (productType && productType !== ALL_ENTRIES_FILTER_VALUE && league.product !== productType) {
        return acc
      }
      acc[id] = league
      return acc
    }, {})
  }, [ mergedLeagues, textSearch, productType ])

  const setMassDates = useCallback((leagueID, dates) => {
    dispatch(updateLeagueConfig(leagueID, { mass_dates: dates }))
  }, [ dispatch ])

  const addDateToMassDates = useCallback((leagueID, date) => {
    const currentMassDates = sessionData?.leagues?.[leagueID]?.mass_dates || []

    // TODO: persist the data for rounds in the session (NEEDED FOR VALIDATION)
    const leagueRounds = mergedLeagues?.[leagueID]?.rounds || []

    if (currentMassDates.length + 1 > leagueRounds.length) {
      return
    }

    setMassDates(leagueID, [ ...currentMassDates, date ])
  }, [ sessionData, mergedLeagues, setMassDates ])

  const removeDateFromMassDates = useCallback((leagueID, date) => {
    const currentMassDates = sessionData?.leagues?.[leagueID]?.mass_dates || []
    const firstMatch = currentMassDates.indexOf(date)
    if (firstMatch === -1) {
      return
    }
    setMassDates(leagueID, currentMassDates.filter((_, index) => index !== firstMatch))
  }, [ sessionData, setMassDates ])

  const prePopulateMassDates = useCallback(() => {
    const today = todayUTC()

    Object.entries(mergedLeagues).forEach(([ leagueID, league ]) => {
      const rounds = league?.rounds || []
      const currentMassDates = mergedLeagues?.[leagueID]?.mass_dates

      if (!rounds.length || currentMassDates?.length) {
        return
      }

      const roundDates = rounds
        .map(round => round.playAt)
        .sort()

      const firstRoundDate = parseDateUTC(roundDates[0])

      if (firstRoundDate >= today) {
        setMassDates(leagueID, roundDates)
      } else {
        const newStartDate = calculateFutureEquivalentDate(firstRoundDate, today)
        const translatedDates = translateTimeSeries(roundDates, newStartDate)
        const dates = translatedDates.map(d => toISODateString(d))
        setMassDates(leagueID, dates)
      }
    })
  }, [ mergedLeagues, setMassDates ])

  const saveEnhancedLeaguesToSession = useCallback(() => {
    if (!enhancedLeagues?.length) return
    dispatch((innerDispatch, getState) => {
      syncEnhancedLeaguesToSession(enhancedLeagues)(innerDispatch, getState)
      const updatedSession = selectCloneLeaguesSession(getState())
      innerDispatch(saveSession({
        cloneLeaguesSessionID: updatedSession?.id,
        data: { ...updatedSession?.data, cloningStep: cloningStep },
      }))
    })
  }, [ dispatch, enhancedLeagues, cloningStep ])

  const startSessionJobs = useCallback(() => {
    dispatch((innerDispatch, getState) => {
      if (cloningStep === CloningStepsEnum.CONFIG) {
        updateAllLeaguesConfigs({ ...configsOptions, season: configsSeason.value })(innerDispatch, getState)
      }
      const updatedSession = selectCloneLeaguesSession(getState())
      innerDispatch(startCloneLeaguesSession({
        cloneLeaguesSessionID: updatedSession?.id,
        data: { ...updatedSession?.data, cloningStep: cloningStep },
      }))
    })
  }, [ cloningStep, configsOptions, configsSeason.value, dispatch ])

  return {
    session,
    sessionID,
    sessionData,
    isLoading,
    error,
    mergedLeagues,
    leaguesForDatePicking,
    saveSelectedLeaguesToSession,
    saveConfigsToSession,
    updateNameForLeague: (leagueID, newName) => dispatch(updateLeagueConfig(leagueID, { league_name: newName })),
    saveSessionToServer,
    startSessionJobs,
    setMassDates,
    addDateToMassDates,
    removeDateFromMassDates,
    prePopulateMassDates,
    saveEnhancedLeaguesToSession,
  }
}

export default useSessionSaver
